import { useState, useEffect } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/shared/stores/authStore'
import { useAuth } from '@/lib/use-auth'
import { useRoleCheck } from '@/features/admin/hooks/useRoleCheck'
import { Button } from '@/shared/components/ui/button'
import { membersApi } from '@/features/workspaces/api/membersApi'
import {
  LayoutDashboard,
  Clock,
  FileText,
  Users,
  Settings,
  Shield,
  ChevronLeft,
  ChevronRight,
  Briefcase,
  Building2,
  Puzzle,
  LogOut,
  Cog,
  Mail,
} from 'lucide-react'

interface SidebarProps {
  collapsed: boolean
}

export function Sidebar({ collapsed }: SidebarProps) {
  const { user } = useAuthStore()
  const { toggleSidebar, collapseSidebar } = useUIStore()
  const { logout } = useAuth()
  const navigate = useNavigate()
  const [pendingInvitations, setPendingInvitations] = useState(0)

  useEffect(() => {
    membersApi.getMyInvitations().then((res) => {
      if (res.success) {
        setPendingInvitations(res.data?.invitations?.length ?? 0)
      }
    }).catch(() => {})
  }, [])

  const { isAdmin, role: workspaceRole } = useRoleCheck()

  const navItems: Array<{ to: string; icon: any; label: string; badge?: number }> = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/time-entries', icon: Clock, label: 'Time Entries' },
    { to: '/projects', icon: Briefcase, label: 'Projects' },
    { to: '/clients', icon: Building2, label: 'Clients' },
    { to: '/workspaces', icon: Mail, label: 'Workspaces', badge: pendingInvitations },
    { to: '/reports', icon: FileText, label: 'Reports' },
    { to: '/team', icon: Users, label: 'Team' },
  ]
  
  const workspaceItems = [
    { to: '/workspace/settings', icon: Cog, label: 'Workspace Settings' },
  ]
  
  const adminItems = [
    { to: '/admin', icon: Shield, label: 'Admin' },
    { to: '/integrations', icon: Puzzle, label: 'Integrations' },
  ]
  
  return (
    <div className="flex h-full flex-col">
      {/* Logo */}
      <div className="flex h-16 items-center justify-between gap-3 px-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-gradient shadow-sm">
            <Clock className="h-4 w-4 text-white" />
          </div>
          {!collapsed && (
            <span className="text-base font-semibold tracking-tight">TimeTracker</span>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => collapseSidebar(!collapsed)}
          className="hidden lg:flex h-7 w-7"
        >
          {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
        </Button>
      </div>
      
      {/* Navigation */}
      <nav className="flex-1 space-y-0.5 px-3 py-2">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200
              ${isActive 
                ? 'bg-primary/10 text-primary' 
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              }
              ${collapsed ? 'justify-center' : ''}
            `
            }
            onClick={() => window.innerWidth < 1024 && toggleSidebar()}
          >
            {({ isActive }) => (
              <>
                {isActive && !collapsed && (
                  <span className="absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-full bg-primary" />
                )}
                <item.icon className={`h-5 w-5 shrink-0 transition-all duration-200 ${isActive ? 'opacity-100' : 'opacity-70 group-hover:opacity-100'}`} />
                {!collapsed && (
                  <div className="flex flex-1 items-center justify-between">
                    <span>{item.label}</span>
                    {item.badge != null && item.badge > 0 && (
                      <span className="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-indigo-600 px-1.5 text-[10px] font-bold text-white">
                        {item.badge}
                      </span>
                    )}
                  </div>
                )}
              </>
            )}
          </NavLink>
        ))}
        
        {!collapsed && (
          <div className="my-3 border-t border-border/50" />
        )}
        {workspaceItems.map((item) => (
          <NavLink
            key={item.label}
            to={item.to}
            className={({ isActive }) =>
              `group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200
              ${isActive 
                ? 'bg-primary/10 text-primary' 
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              }
              ${collapsed ? 'justify-center' : ''}
            `
            }
            onClick={() => window.innerWidth < 1024 && toggleSidebar()}
          >
            {({ isActive }) => (
              <>
                {isActive && !collapsed && (
                  <span className="absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-full bg-primary" />
                )}
                <item.icon className={`h-5 w-5 shrink-0 transition-all duration-200 ${isActive ? 'opacity-100' : 'opacity-70 group-hover:opacity-100'}`} />
                {!collapsed && <span>{item.label}</span>}
              </>
            )}
          </NavLink>
        ))}

        {isAdmin && (
          <>
            {!collapsed && (
              <div className="my-3 border-t border-border/50" />
            )}
            {adminItems.map((item) => (
              <NavLink
                key={item.label}
                to={item.to}
                className={({ isActive }) =>
                  `group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200
                  ${isActive 
                    ? 'bg-primary/10 text-primary' 
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  }
                  ${collapsed ? 'justify-center' : ''}
                `
                }
                onClick={() => window.innerWidth < 1024 && toggleSidebar()}
              >
                {({ isActive }) => (
                  <>
                    {isActive && !collapsed && (
                      <span className="absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-full bg-primary" />
                    )}
                    <item.icon className={`h-5 w-5 shrink-0 transition-all duration-200 ${isActive ? 'opacity-100' : 'opacity-70 group-hover:opacity-100'}`} />
                    {!collapsed && <span>{item.label}</span>}
                  </>
                )}
              </NavLink>
            ))}
          </>
        )}
      </nav>
      
      {/* User info */}
      {!collapsed && user && (
        <div className="border-t border-border/50 bg-muted/20">
          <div className="flex items-center gap-2 p-3">
            {user.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.name ?? user.email ?? 'User'}
                className="h-9 w-9 rounded-full object-cover ring-2 ring-border"
              />
            ) : (
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-gradient text-primary-foreground text-sm font-semibold ring-2 ring-primary/20">
                {(user.name ?? user.email ?? '?').charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground">{user.name}</p>
              <div className="flex items-center gap-1.5">
                <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                <span className="inline-flex items-center rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary capitalize">
                  {workspaceRole?.toLowerCase()}
                </span>
              </div>
            </div>
          </div>
          <div className="flex border-t border-border/50">
            <button
              onClick={() => navigate('/settings')}
              className="flex flex-1 items-center justify-center gap-2 py-2.5 text-xs text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              <Settings className="h-4 w-4" />
              <span>My Settings</span>
            </button>
            <div className="w-px bg-border/50" />
            <button
              onClick={logout}
              className="flex flex-1 items-center justify-center gap-2 py-2.5 text-xs text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              <LogOut className="h-4 w-4" />
              <span>Log out</span>
            </button>
          </div>
        </div>
      )}
      {collapsed && user && (
        <div className="border-t border-border/50 p-3 flex flex-col items-center gap-2">
          {user.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt={user.name ?? user.email ?? 'User'}
              className="h-8 w-8 rounded-full object-cover ring-2 ring-border"
            />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-gradient text-primary-foreground text-xs font-semibold ring-2 ring-primary/20">
              {(user.name ?? user.email ?? '?').charAt(0).toUpperCase()}
            </div>
          )}
          <button
            onClick={() => navigate('/settings')}
            className="text-muted-foreground hover:text-foreground transition-colors"
            title="Settings"
          >
            <Settings className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  )
}

// Need to import useUIStore
import { useUIStore } from '@/shared/stores/uiStore'

export default Sidebar
